/**
 * Componente: ClientAlertsTable
 * Tabla de alertas de clientes en sus servicios multi-nube
 * Paleta: Kloud OPS (violeta + acentos cyan)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';

// Semáforo visual (mismo criterio que el chat de operaciones)
const SEVERITY_STYLES = {
  CRITICAL: { color: '#ef4444', bg: 'rgba(239,68,68,0.14)', border: 'rgba(239,68,68,0.5)' },
  HIGH: { color: '#f97316', bg: 'rgba(249,115,22,0.13)', border: 'rgba(249,115,22,0.45)' },
  MEDIUM: { color: '#facc15', bg: 'rgba(250,204,21,0.12)', border: 'rgba(250,204,21,0.4)' },
  LOW: { color: '#4ade80', bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.4)' },
  INFO: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.4)' }
};

// Colores por proveedor (mismos que el mapa)
const CLOUD_COLORS = {
  aws: '#fb923c',
  azure: '#38bdf8',
  gcp: '#34d399',
  huawei: '#f87171',
  m365: '#f472b6'
};

export default function ClientAlertsTable({ alerts = [], loading = false, maxHeight = '520px', onRowClick }) {
  const [sortBy, setSortBy] = useState('event_ts');
  const [sortOrder, setSortOrder] = useState('desc');

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
  };

  const sorted = [...alerts].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (sortBy === 'event_ts') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (aVal === bVal) return 0;
    const cmp = aVal > bVal ? 1 : -1;
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const formatDate = (alert) => {
    if (alert.event_ts) return new Date(alert.event_ts).toLocaleString('es-MX');
    if (alert.fecha) return `${alert.fecha}${alert.hora ? ` ${alert.hora}` : ''}`;
    return '-';
  };

  const SortIcon = ({ col }) =>
    sortBy === col
      ? (sortOrder === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
      : null;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-violet-400" />
          <span className="text-sm text-violet-200/70">Cargando alertas de clientes...</span>
        </div>
      </div>
    );
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-cyan-300/60" />
          <p className="text-sm font-semibold text-violet-100">Sin alertas de clientes</p>
          <p className="mt-1 text-xs text-violet-200/60">
            Los servicios de los clientes operan con normalidad en todas las nubes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-scrollbar overflow-auto" style={{ maxHeight }}>
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-[#160a2e] text-xs uppercase tracking-wider text-violet-200/70">
          <tr className="border-b border-violet-500/20">
            <th className="px-5 py-3 text-left">
              <button onClick={() => toggleSort('event_ts')} className="inline-flex items-center gap-1 hover:text-cyan-300">
                Fecha / Hora <SortIcon col="event_ts" />
              </button>
            </th>
            <th className="px-5 py-3 text-left">Nube</th>
            <th className="px-5 py-3 text-left">Servicio</th>
            <th className="px-5 py-3 text-left">Recurso</th>
            <th className="px-5 py-3 text-left">Proyecto ID</th>
            <th className="px-5 py-3 text-left">Cliente</th>
            <th className="px-5 py-3 text-left">Región</th>
            <th className="px-5 py-3 text-left">
              <button onClick={() => toggleSort('severity_score')} className="inline-flex items-center gap-1 hover:text-cyan-300">
                Severidad <SortIcon col="severity_score" />
              </button>
            </th>
            <th className="px-5 py-3 text-left">Estado</th>
            <th className="px-5 py-3 text-center">Activa</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((alert, idx) => {
            const severity = (alert.severity_normalized || 'INFO').toUpperCase();
            const sevStyle = SEVERITY_STYLES[severity] || SEVERITY_STYLES.INFO;
            const cloud = alert.nube || alert.provider || '-';
            const cloudColor = CLOUD_COLORS[cloud.toLowerCase()] || '#a78bfa';

            return (
              <tr
                key={`${alert.resource_id || alert.resource_name || 'row'}-${idx}`}
                className="border-b border-violet-500/10 transition-colors hover:bg-violet-500/15 cursor-pointer"
                onClick={() => onRowClick && onRowClick(alert)}
                title="Click para ver detalle"
              >
                <td className="whitespace-nowrap px-5 py-3 text-xs text-violet-200/80">{formatDate(alert)}</td>
                <td className="px-5 py-3">
                  <span
                    className="rounded-full px-2.5 py-0.5 text-xs font-bold"
                    style={{ color: cloudColor, background: `${cloudColor}1f`, border: `1px solid ${cloudColor}55` }}
                  >
                    {cloud}
                  </span>
                </td>
                <td className="px-5 py-3 font-medium text-violet-100">{alert.service || '-'}</td>
                <td className="max-w-[220px] truncate px-5 py-3 text-violet-200/80" title={alert.resource_name || ''}>
                  {alert.resource_name || '-'}
                </td>
                <td className="max-w-[160px] truncate px-5 py-3 text-xs text-violet-200/70" title={alert.project_id || ''}>
                  {alert.project_id || '-'}
                </td>
                <td className="max-w-[180px] truncate px-5 py-3 text-xs font-medium text-cyan-200/90" title={alert.cliente || ''}>
                  {alert.cliente || '-'}
                </td>
                <td className="px-5 py-3 text-violet-200/70">{alert.region || '-'}</td>
                <td className="px-5 py-3">
                  <span
                    className="rounded-full px-2.5 py-0.5 text-xs font-bold"
                    style={{ color: sevStyle.color, background: sevStyle.bg, border: `1px solid ${sevStyle.border}` }}
                  >
                    {severity}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-violet-200/80">{alert.status_normalized || '-'}</td>
                <td className="px-5 py-3 text-center">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${alert.is_active ? 'bg-rose-400' : 'bg-emerald-400/70'}`}
                    title={alert.is_active ? 'Activa' : 'Resuelta'}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
